import React, { useState, useEffect } from 'react';
import Translator from '../../core/i18n';
import moment from 'moment';

const T = Translator.create();

interface IParsedWatchedData {
    data: number;
    durationSeconds: string;
    status: boolean;
}

type VideoBarProps = {
    videoInformation?: { videoTestLogId: string; enrollmentId?: string | null; seconds: number; viewData?: string | null };
    duration: number;
};

const VideoBar: React.FC<VideoBarProps> = ({ videoInformation, duration }) => {
    const [parsedWatchedData, setParsedWatchedData] = useState<IParsedWatchedData[]>([]);
    const [totalWatchedPercentage, setTotalWatchedPercentage] = useState<number>(0);
    const [enableTimePeriod, setEnableTimePeriod] = useState<boolean>(true);

    const formatTime = (seconds: number): string => {
        const duration = moment.duration(seconds, 'seconds');
        let formattedTime = '';

        if (duration.hours() > 0) {
            formattedTime += `${duration
                .hours()
                .toString()
                .padStart(2, '0')}:`;
        }
        if (duration.minutes() > 0 || duration.hours() > 0) {
            formattedTime += `${duration
                .minutes()
                .toString()
                .padStart(2, '0')}:`;
        }

        formattedTime += duration
            .seconds()
            .toString()
            .padStart(2, '0');

        if (formattedTime === '') {
            formattedTime = '00:00';
        } else if (!formattedTime.includes(':')) {
            formattedTime = '00:' + formattedTime;
        }

        return formattedTime;
    };

    const calculatePercentage = (end: number, start: number): number => {
        return ((end - start) * 100) / duration;
    };
    const timePeriod = (start: string, end: string): string => {
        if (start === end) {
            return '';
        }

        return start + '-' + end;
    };

    const viewBar = (barData: IParsedWatchedData[]): JSX.Element[] => {
        let returnedBar: JSX.Element[] = [];

        for (let i = 0; i < barData.length; i++) {
            returnedBar.push(
                <div
                    key={i}
                    className={'progress-bar ' + (barData[i].status ? 'progress-bar-green' : 'progress-bar-red')}
                    style={{ width: `${barData[i].data}%` }}
                    role="progressbar"
                    aria-valuenow={0}
                    aria-valuemin={0}
                ></div>
            );
        }
        return returnedBar;
    };

    const viewTimePeriod = (timePeriodData: IParsedWatchedData[]): JSX.Element => {
        return (
            <div className="row new-activity-padding d-flex align-items-center">
                <div className="col-3">
                    <span className="progress-bar-text mobile-none">{T.t('r_un_watched_times_l')}</span>
                    <span className="mobile-displayed-time web-none">{T.t('r_un_watched_times_m')}</span>
                    <span className="mobile-displayed-time web-none">{T.t('r_activity_time')}</span>
                </div>
                <div className="col-9">
                    {timePeriodData.map(item => {
                        return <div className="col-12 progress-bar-text-times">{item.durationSeconds}</div>;
                    })}
                </div>
            </div>
        );
    };

    const parseVideoData = (data?: string | null): void => {
        let videoWatchedData: IParsedWatchedData[] = [];
        let watchedPercentage: number = 0;
        let dataWatchedPercentage: number;
        if (duration === 0 || !data || !data.includes('-')) {
            videoWatchedData.push({ data: 100, durationSeconds: duration ? timePeriod('0', formatTime(duration)) : '', status: false });
            if (duration === 0) {
                setEnableTimePeriod(false);
            }
        } else {
            
            let parsedViewData: string[] = [];
            try {
                parsedViewData = JSON.parse(data);
                if (typeof parsedViewData === 'string') {
                    parsedViewData = [parsedViewData];
                }
            } catch {
                if (typeof data === 'string' && data.includes('-')) {
                    parsedViewData = [data];
                }
            }
            
            for (let i = 0; i < parsedViewData.length; i++) {
                const rangeParts = parsedViewData[i].split('-');
                if (parsedViewData[i + 1]) {
                    const rangePartsNext = parsedViewData[i + 1].split('-');
                    dataWatchedPercentage = calculatePercentage(parseInt(rangeParts[1]), parseInt(rangeParts[0]));
                    watchedPercentage += dataWatchedPercentage;
                    videoWatchedData.push(
                        { data: dataWatchedPercentage, durationSeconds: '', status: true },
                        {
                            data: calculatePercentage(parseInt(rangePartsNext[0]), parseInt(rangeParts[1])),
                            durationSeconds: timePeriod(formatTime(parseInt(rangeParts[1])), formatTime(parseInt(rangePartsNext[0]))),
                            status: false
                        }
                    );
                } else {
                    if (parseInt(rangeParts[1]) >= duration + 1) {
                        watchedPercentage = 100;
                        videoWatchedData.push({ data: 100, durationSeconds: '', status: true });
                    } else {
                        dataWatchedPercentage = calculatePercentage(parseInt(rangeParts[1]), parseInt(rangeParts[0]));
                        watchedPercentage += dataWatchedPercentage;
                        videoWatchedData.push(
                            { data: dataWatchedPercentage, durationSeconds: '', status: true },
                            {
                                data: calculatePercentage(duration, parseInt(rangeParts[1])),
                                durationSeconds: timePeriod(formatTime(parseInt(rangeParts[1])), formatTime(duration)),
                                status: false
                            }
                        );
                    }
                }
            }
        }

        setParsedWatchedData(videoWatchedData);
        setTotalWatchedPercentage(Math.floor(watchedPercentage));
    };

    useEffect(() => {
        parseVideoData(videoInformation?.viewData);
    }, []);

    useEffect(() => {
        if (totalWatchedPercentage === 100) {
            setEnableTimePeriod(false);
        }
    }, [totalWatchedPercentage]);

    return (
        <>
            <div className="row new-activity-padding d-flex align-items-center">
                <div className="col-3">
                    <span className="progress-bar-text mobile-none">{T.t('r_activity_showed_time')}</span>
                    <span className="mobile-displayed-time web-none">{T.t('r_activity_showed')}</span>
                    <span className="mobile-displayed-time web-none">{T.t('r_activity_time')}</span>
                </div>
                <div className="col-7">
                    <div className="e-lesson-progress" style={{ width: '100%' }}>
                        <div className="progress">{viewBar(parsedWatchedData)}</div>
                    </div>
                </div>
                <div className="col-2 px-0 d-flex flex-column">
                    <span className="progress-percent">{totalWatchedPercentage}%</span>
                    <span className="progress-time">
                        <b> {videoInformation ? moment.utc(videoInformation.seconds * 1000).format('HH:mm:ss') : '00:00:00'} </b>/{moment.utc(duration * 1000).format('HH:mm:ss')}
                    </span>
                </div>
            </div>
            {enableTimePeriod && viewTimePeriod(parsedWatchedData)}
        </>
    );
};

export default VideoBar;
